<template>
  <platform-modal
    :value="value"
    icon="bell"
    :title="$t('page.viewSurvey.responsesStatus.manageRemindersTitle')"
    :width="800"
    :loading="saving"
    :confirm-disabled="saving || !isFormValid"
    @confirm="save"
    @close="$emit('close')"
  >
    <p class="mb-4">
      {{ $t('page.viewSurvey.responsesStatus.manageRemindersDescription') }}
    </p>

    <v-row>
      <v-col :cols="12" :md="4">
        <v-select
          v-model="mode"
          :items="[
            { text: $t('page.viewSurvey.responsesStatus.reminderModeEndDate'), value: ReminderMode.END_DATE },
            { text: $t('page.viewSurvey.responsesStatus.reminderModeFrequency'), value: ReminderMode.FREQUENCY }
          ]"
          :label="$t('page.viewSurvey.responsesStatus.reminderMode')"
          outlined
          dense
          hide-details
        />
      </v-col>
      <v-col v-if="mode === ReminderMode.END_DATE" :cols="12" :md="4">
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endDateDisplay"
              :label="$t('page.viewSurvey.responsesStatus.reminderEndDate')"
              prepend-inner-icon="mdi-calendar"
              readonly
              clearable
              outlined
              dense
              :error-messages="endDateError"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="endDate"
            :min="minEndDate"
            no-title
            scrollable
            @input="endDateMenu = false"
          />
        </v-menu>
      </v-col>
      <v-col v-if="mode === ReminderMode.FREQUENCY" :cols="12" :md="4">
        <v-text-field
          v-model.number="frequencyDays"
          :label="$t('page.viewSurvey.responsesStatus.reminderFrequencyDays')"
          type="number"
          :min="1"
          outlined
          dense
          :error-messages="frequencyDaysError"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col>
        <span
          v-if="lastTriggeredDate"
          class="text-body-2 grey--text text--darken-1"
        >
          {{ $t('page.viewSurvey.responsesStatus.lastReminderSent', [formatDate(lastTriggeredDate)]) }}
        </span>
      </v-col>
      <v-col :cols="12" :sm="5" :md="2" class="text-center">
        <div class="mb-2 text-body-2 grey--text text--darken-1 text-no-wrap">
          {{ $t('page.viewSurvey.responsesStatus.selectAllReminders') }}
        </div>
        <v-switch
          :input-value="allSelected"
          hide-details
          class="mt-0 d-inline-block"
          @change="toggleAll"
        />
      </v-col>
    </v-row>
    <v-row
      v-for="individual in individuals"
      :key="individual.id"
      class="mb-2"
      align="center"
      no-gutters
    >
      <v-col>
        <platform-entity-card
          entity-type="individual"
          row
          flat
          compact
          align="start"
          :entity="individual"
        />
      </v-col>
      <v-col :cols="12" :sm="5" :md="2" class="text-center">
        <v-switch
          :input-value="isIncluded(individual.id)"
          :disabled="saving"
          hide-details
          class="mt-0 d-inline-block"
          @change="toggleIndividual(individual.id)"
        />
      </v-col>
    </v-row>
    <v-row v-if="showOriginalEndDateInfo" no-gutters class="mt-4">
      <v-col :cols="12">
        <span class="text-body-2 grey--text text--darken-1">
          {{ $t('page.viewSurvey.responsesStatus.originalEndDateInfo', [formatDate(endDate)]) }}
        </span>
      </v-col>
    </v-row>
  </platform-modal>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import dayjs from 'dayjs'
import API from '@/services/API'
import { updateSurveyGroupAutomaticReminders } from '@/graphql/custom/updateSurveyGroupAutomaticReminders'
import { ReminderMode } from '@betterboards/shared/types/API'
import { REMINDERS_ENABLED_STATUS, REMINDERS_DISABLED_STATUS } from '@betterboards/shared/constants/reminders'
import { PlatformAutomaticRemindersConfiguration, PlatformSurveyGroup } from '@/types/SurveyGroup'
import { ResultsIndividual } from '@betterboards/shared/types/ResultSet'
import PlatformModal from '@/components/PlatformModal.vue'
import PlatformEntityCard from '@/components/PlatformEntityCard.vue'

export default Vue.extend({
  name: 'ManageRemindersModal',
  components: {
    PlatformModal,
    PlatformEntityCard
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    surveyGroup: {
      type: Object as PropType<PlatformSurveyGroup | undefined>,
      default: undefined
    },
    individuals: {
      type: Array as PropType<ResultsIndividual[]>,
      default: () => []
    }
  },
  data: () => ({
    saving: false,
    includedIds: [] as string[],
    mode: null as ReminderMode | null,
    endDate: null as string | null,
    frequencyDays: null as number | null,
    frequencyModeStartedAt: null as string | null,
    endDateMenu: false,
    ReminderMode
  }),
  computed: {
    minEndDate (): string {
      return dayjs().format('YYYY-MM-DD')
    },
    endDateDisplay: {
      get (): string | null {
        if (!this.endDate) {
          return null
        }
        return dayjs(this.endDate).format('DD MMM YYYY')
      },
      set (value: string | null): void {
        if (!value) {
          this.endDate = null
        }
      }
    },
    allSelected (): boolean {
      if (!this.individuals.length) {
        return false
      }
      return this.individuals.every((ind) =>
        this.includedIds.includes(ind.id)
      )
    },
    showOriginalEndDateInfo (): boolean {
      return this.mode === ReminderMode.FREQUENCY && this.endDate !== null
    },
    lastTriggeredDate (): string | null {
      const reminders = this.surveyGroup?.automaticReminders
      if (!reminders) {
        return null
      }

      return reminders.lastReminderSentAt ?? reminders.firstReminderSentAt ?? null
    },
    endDateError (): string | null {
      if (this.mode === ReminderMode.END_DATE && !this.endDate) {
        return this.$t('page.viewSurvey.responsesStatus.reminderEndDateRequired') as string
      }
      return null
    },
    frequencyDaysError (): string | null {
      if (this.mode === ReminderMode.FREQUENCY) {
        if (this.frequencyDays === null || this.frequencyDays === undefined) {
          return this.$t('page.viewSurvey.responsesStatus.reminderFrequencyRequired') as string
        }
        if (!Number.isInteger(this.frequencyDays) || this.frequencyDays < 1) {
          return this.$t('page.viewSurvey.responsesStatus.reminderFrequencyInvalid') as string
        }
      }
      return null
    },
    isFormValid (): boolean {
      if (!this.includedIds.length) {
        return true
      }
      if (this.mode === ReminderMode.END_DATE && !this.endDate) {
        return false
      }
      if (this.mode === ReminderMode.FREQUENCY) {
        if (this.frequencyDays === null || this.frequencyDays === undefined) {
          return false
        }
        if (!Number.isInteger(this.frequencyDays) || this.frequencyDays < 1) {
          return false
        }
      }
      return true
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (newValue: boolean): void {
        if (newValue) {
          this.initFromSurveyGroup()
        }
      }
    },
    mode (newMode: ReminderMode | null, oldMode: ReminderMode | null): void {
      // If user manually switches from END_DATE to FREQUENCY, clear the endDate
      if (oldMode === ReminderMode.END_DATE && newMode === ReminderMode.FREQUENCY) {
        this.endDate = null
      }
    }
  },
  methods: {
    formatDate (date: string | null): string | null {
      if (!date) {
        return null
      }
      return dayjs(date).format('DD MMM YYYY')
    },
    initFromSurveyGroup (): void {
      const reminders = this.surveyGroup?.automaticReminders
      const existingIds = reminders?.includedIndividualIds ?? []
      this.includedIds = existingIds.filter((id): id is string => id !== null)
      this.mode = reminders?.mode ?? ReminderMode.END_DATE
      this.endDate = reminders?.endDate ? reminders.endDate.split('T')[0] : null
      this.frequencyDays = reminders?.frequencyDays ?? null
      this.frequencyModeStartedAt = reminders?.frequencyModeStartedAt ?? null
    },
    isIncluded (individualId: string): boolean {
      return this.includedIds.includes(individualId)
    },
    toggleIndividual (individualId: string): void {
      const index = this.includedIds.indexOf(individualId)
      if (index === -1) {
        this.includedIds.push(individualId)
      } else {
        this.includedIds.splice(index, 1)
      }
    },
    toggleAll (enabled: boolean): void {
      if (enabled) {
        this.includedIds = this.individuals.map((ind) => ind.id)
      } else {
        this.includedIds = []
      }
    },
    async save (): Promise<void> {
      if (!this.surveyGroup?.id) {
        this.$toasted.error(this.$t('page.viewSurvey.responsesStatus.saveRemindersError') as string)
        return
      }

      if (!this.isFormValid) {
        return
      }

      if (this.mode === ReminderMode.FREQUENCY) {
        if (this.frequencyDays === null || !Number.isInteger(this.frequencyDays) || this.frequencyDays < 1) {
          this.$toasted.error(this.$t('page.viewSurvey.responsesStatus.reminderFrequencyInvalid') as string)
          return
        }
      }

      this.saving = true
      try {
        const automaticReminders: PlatformAutomaticRemindersConfiguration = {
          mode: this.mode,
          endDate: this.endDate ? new Date(this.endDate).toISOString() : null,
          frequencyDays: this.frequencyDays,
          includedIndividualIds: this.includedIds,
          frequencyModeStartedAt: this.mode === ReminderMode.FREQUENCY
            ? new Date().toISOString()
            : null
        }

        const hasReminders = this.includedIds && this.includedIds.length > 0

        const input = {
          id: this.surveyGroup.id,
          automaticReminders,
          remindersEnabled: hasReminders ? REMINDERS_ENABLED_STATUS : REMINDERS_DISABLED_STATUS
        }

        await API.graphql({
          query: updateSurveyGroupAutomaticReminders,
          variables: {
            input
          }
        })

        this.$emit('saved', {
          ...automaticReminders,
          __typename: 'AutomaticRemindersConfiguration'
        })
        this.$toasted.success(this.$t('page.viewSurvey.responsesStatus.saveRemindersSuccess') as string)
        this.$emit('close')
      } catch (err) {
        console.error('Failed to update automatic reminders:', err)
        this.$toasted.error(this.$t('page.viewSurvey.responsesStatus.saveRemindersError') as string)
      } finally {
        this.saving = false
      }
    }
  }
})
</script>
